import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  FileTreeEntry,
  FileContent,
  FileTreeRefreshHandle,
  FileTabsEditing,
  FileEditingState,
  FileEditingResolveAction,
} from '@localagent/ui';
import { FileWriteConflictError } from '@localagent/sdk';
import type { WorkspaceRoutingClass } from '@localagent/sdk';
import { useWorkspaceFiles } from './useWorkspaceFiles';
import { useOpenExternally } from './useOpenExternally';
import { workspaceWorkstationHref } from '../components/workspaces/workspaceLinks';

export type FileBrowserNotice = {
  tone: 'muted' | 'warning' | 'error';
  message: string;
  canRetry: boolean;
  action?: { label: string; href: string };
};

/**
 * Host-owned per-open-file state. The LIVE
 * text is deliberately NOT here — it lives in the CM6 view and reaches this
 * hook's buffer refs only at sync points (debounce boundary, save, tab
 * switch, close) so a keystroke never causes a host re-render.
 */
export interface OpenFileState extends FileEditingState {
  /** Version token the buffer was loaded/saved at (CAS currency). */
  baseVersion: string | null;
  /**
   * Autosave suspension: ANY save failure suspends
   * autosave for this tab until a user act (manual save / retry / conflict
   * resolution) — no failing-write-per-idle-gap loops.
   */
  autosaveSuspended: boolean;
}

type SaveKind = 'manual' | 'autosave' | 'keep-mine' | 'save-as-new';

const INITIAL_FILE_STATE: OpenFileState = {
  baseVersion: null,
  dirty: false,
  saving: false,
  saveError: null,
  externalChange: null,
  notFound: false,
  autosaveSuspended: false,
  epoch: 0,
};

/**
 * Shared workspace-file state for the SPLIT chat file browser: the directory
 * tree and the tabbed viewer are separate components (rendered in separate
 * layout slots), so their common state — backend session/daemon wiring, the
 * root listing, and the VSCode-style set of OPEN files — is lifted here and
 * consumed by both.
 *
 * This owns what `WorkspaceFileBrowser` used to own internally (root load,
 * via {@link useWorkspaceFiles}), ADDS the open-files model
 * (`openPaths` + `activePath`), and the
 * per-tab buffer/dirty/version model with the CAS save path and idle
 * autosave, for BOTH modes: cloud saves ride the sandbox CAS write, local
 * saves ride the daemon's `local_file_write` CAS tool (same conflict flow).
 */
export function useWorkspaceFileBrowser({
  workspaceId,
  workspacePath,
  mode,
  onEntriesDiscovered,
}: {
  workspaceId: string;
  workspacePath?: string;
  mode?: WorkspaceRoutingClass;
  onEntriesDiscovered?: (entries: FileTreeEntry[]) => void;
}) {
  const isLocal = mode === 'local';
  const [rootEntries, setRootEntries] = useState<FileTreeEntry[] | null>(null);
  const [rootError, setRootError] = useState<string | null>(null);
  const rootLoadedRef = useRef(false);
  const [rootLoadAttempt, setRootLoadAttempt] = useState(0);
  const rootLoadGenerationRef = useRef(0);

  const [openPaths, setOpenPaths] = useState<string[]>([]);
  const [activePath, setActivePath] = useState<string | null>(null);

  // Per-open-path editing state. React state so tabs/banners
  // render from it; mirrored in a ref for the async save/refresh paths.
  const [fileStates, setFileStates] = useState<Record<string, OpenFileState>>({});
  const fileStatesRef = useRef(fileStates);
  fileStatesRef.current = fileStates;
  const buffersRef = useRef<Map<string, string>>(new Map());
  const savedTextRef = useRef<Map<string, string>>(new Map());
  // (app-shell ADR-530ad)
  const heldPrefixesRef = useRef<Set<string>>(new Set());
  const deferredSavesRef = useRef<Map<string, SaveKind>>(new Map());
  const listingEpochRef = useRef(0);
  const movedPathAliasRef = useRef<Map<string, string>>(new Map());
  const resolvePath = useCallback((path: string) => movedPathAliasRef.current.get(path) ?? path, []);
  const isHeld = useCallback((path: string) => {
    for (const prefix of heldPrefixesRef.current) {
      if (path === prefix || path.startsWith(`${prefix}/`)) return true;
    }
    return false;
  }, []);
  // Wall-clock time of this host's last SUCCESSFUL write (self-echo
  // suppression): the cloud-push subscription checks it at
  // debounce-fire time and skips the refresh when the frame's only plausible
  // delta is our own save. A ref, never state — it must not re-render.
  const lastOwnSaveAtRef = useRef(0);

  const updateFileState = useCallback((path: string, patch: Partial<OpenFileState>) => {
    setFileStates((prev) => {
      const cur = prev[path];
      if (!cur) return prev;
      return { ...prev, [path]: { ...cur, ...patch } };
    });
  }, []);

  const sizeByPath = useRef<Map<string, number>>(new Map());
  // Latest known content-version token per file path (from list entries) — the
  // refresh diff currency and the per-tab cache validity check.
  const versionByPath = useRef<Map<string, string>>(new Map());
  const listedChildrenByDir = useRef<Map<string, Set<string>>>(new Map());
  const rememberSizes = useCallback((entries: FileTreeEntry[]) => {
    for (const e of entries)
      if (!e.isDir && typeof e.size === 'number') sizeByPath.current.set(e.path, e.size);
  }, []);
  const rememberVersions = useCallback((entries: FileTreeEntry[]) => {
    for (const e of entries) {
      if (e.isDir) continue;
      if (typeof e.version === 'string') versionByPath.current.set(e.path, e.version);
      else versionByPath.current.delete(e.path);
    }
  }, []);
  const rememberListing = useCallback((dir: string, entries: FileTreeEntry[]) => {
    listedChildrenByDir.current.set(dir, new Set(entries.map((e) => e.path)));
  }, []);

  const { state, error, listDir, readFile, writeFile, createFile: createFileExclusive, deleteFile, moveFile, createFolder, resume } =
    useWorkspaceFiles(workspaceId, workspacePath ?? null, mode);
  const { openExternally, platformType } = useOpenExternally();

  const resetWorkspaceIdRef = useRef(workspaceId);
  useEffect(() => {
    if (resetWorkspaceIdRef.current === workspaceId) return;
    resetWorkspaceIdRef.current = workspaceId;
    rootLoadGenerationRef.current += 1;
    rootLoadedRef.current = false;
    setRootEntries(null);
    setRootError(null);
    setOpenPaths([]);
    setActivePath(null);
    setFileStates({});
    buffersRef.current.clear();
    savedTextRef.current.clear();
    heldPrefixesRef.current.clear();
    deferredSavesRef.current.clear();
    movedPathAliasRef.current.clear();
    versionByPath.current.clear();
    sizeByPath.current.clear();
    listedChildrenByDir.current.clear();
  }, [workspaceId]);

  useEffect(() => {
    if (rootLoadedRef.current) return;
    if (state !== 'ready') return;
    rootLoadedRef.current = true;
    const generation = rootLoadGenerationRef.current;
    const epochAtStart = listingEpochRef.current;
    listDir('')
      .then((entries) => {
        if (rootLoadGenerationRef.current !== generation) return;
        if (listingEpochRef.current !== epochAtStart) {
          rootLoadedRef.current = false;
          setRootLoadAttempt((n) => n + 1);
          return;
        }
        setRootEntries(entries);
        rememberSizes(entries);
        rememberVersions(entries);
        rememberListing('', entries);
        onEntriesDiscovered?.(entries);
      })
      .catch((e: unknown) => {
        if (rootLoadGenerationRef.current === generation) {
          rootLoadedRef.current = false;
          setRootError(e instanceof Error ? e.message : String(e));
        }
      });
  }, [state, rootLoadAttempt, listDir, onEntriesDiscovered, rememberSizes, rememberVersions, rememberListing]);

  const loadChildren = useCallback(
    async (path: string): Promise<FileTreeEntry[]> => {
      const epochAtStart = listingEpochRef.current;
      const entries = await listDir(path);
      if (listingEpochRef.current === epochAtStart) {
        rememberSizes(entries);
        rememberVersions(entries);
        rememberListing(path, entries);
      }
      onEntriesDiscovered?.(entries);
      return entries;
    },
    [listDir, onEntriesDiscovered, rememberSizes, rememberVersions, rememberListing]
  );

  /**
   * Viewer read with the per-tab cache: a DIRTY tab never
   * re-reads (the buffer IS the content); a clean tab whose `baseVersion`
   * still matches the latest listed version skips the network round trip. A
   * fresh read of a text file seeds the buffer + version anchors.
   */
  const read = useCallback(
    async function readTab(path: string): Promise<FileContent> {
      const meta = fileStatesRef.current[path];
      const buf = buffersRef.current.get(path);
      if (meta && buf !== undefined) {
        if (meta.dirty) return { content: buf, encoding: 'utf8' };
        if (meta.baseVersion !== null && versionByPath.current.get(path) === meta.baseVersion) {
          return { content: buf, encoding: 'utf8' };
        }
      }
      const epochAtStart = listingEpochRef.current;
      const r = await readFile(path);
      if (listingEpochRef.current !== epochAtStart) return readTab(path);
      if (r.encoding === 'utf8' && fileStatesRef.current[path]) {
        buffersRef.current.set(path, r.content);
        savedTextRef.current.set(path, r.content);
        if (typeof r.version === 'string') versionByPath.current.set(path, r.version);
        updateFileState(path, { baseVersion: r.version ?? null, dirty: false, notFound: false });
      }
      return { content: r.content, encoding: r.encoding };
    },
    [readFile, updateFileState]
  );

  const openExternal = useCallback(
    (path: string, bytes: Uint8Array) => openExternally(path, bytes),
    [openExternally]
  );

  const openFile = useCallback((path: string) => {
    movedPathAliasRef.current.delete(path);
    setOpenPaths((prev) => (prev.includes(path) ? prev : [...prev, path]));
    setFileStates((prev) => (prev[path] ? prev : { ...prev, [path]: INITIAL_FILE_STATE }));
    setActivePath(path);
  }, []);

  const closeFile = useCallback((path: string) => {
    for (const [alias, target] of movedPathAliasRef.current) {
      if (alias === path || target === path) movedPathAliasRef.current.delete(alias);
    }
    buffersRef.current.delete(path);
    savedTextRef.current.delete(path);
    setFileStates((prev) => {
      if (!(path in prev)) return prev;
      const next = { ...prev };
      delete next[path];
      return next;
    });
    setOpenPaths((prev) => {
      const idx = prev.indexOf(path);
      if (idx === -1) return prev;
      const next = prev.filter((p) => p !== path);
      setActivePath((cur) => {
        if (cur !== path) return cur;
        if (next.length === 0) return null;
        return next[Math.max(0, idx - 1)] ?? next[0];
      });
      return next;
    });
  }, []);

  const setActive = useCallback((path: string) => setActivePath(path), []);

  // --- save path ---------------------------------------------------------------
  // Synchronous in-flight guard: the React-state `saving` flag is stale within
  // a single event (Cmd/Ctrl+S and the dialog's Save-&-close flush a sync —
  // whose autosave starts a save — and then request a manual save in the same
  // tick). A caller finding a save in flight JOINS it (returns its outcome)
  // instead of writing twice or being dropped: "Save & close" must observe
  // the flush-triggered autosave's success to close the tab. The state
  // `saving` flag only drives UI.
  const savingPromisesRef = useRef<Map<string, Promise<boolean>>>(new Map());
  const doSaveFile = useCallback(
    async (
      path: string,
      kind: 'manual' | 'autosave' | 'keep-mine' | 'save-as-new'
    ): Promise<boolean> => {
      const meta = fileStatesRef.current[path];
      if (!meta) return false;
      const buffer = buffersRef.current.get(path);
      if (buffer === undefined) return false; // nothing loaded — nothing to save
      let expected: string | undefined;
      if (kind === 'keep-mine') {
        // ui ADR-003: the resolution click IS the save — CAS against the version
        // the conflict reported, never left for autosave.
        const cv = meta.externalChange?.currentVersion;
        if (cv == null) {
          updateFileState(path, {
            saveError: 'The file no longer exists — use "save as new" instead',
          });
          return false;
        }
        expected = cv;
      } else if (kind === 'save-as-new') {
        // Explicit user act: unconditional write recreates the file.
        expected = undefined;
      } else {
        if (meta.baseVersion === null) {
          updateFileState(path, {
            saveError: 'No version token for this file — close and reopen the tab',
          });
          return false;
        }
        expected = meta.baseVersion;
      }
      updateFileState(path, { saving: true, saveError: null });
      try {
        const res = await writeFile(path, buffer, expected);
        // Self-echo currency: the cloud-push subscription
        // skips a push-triggered refresh arriving within a short window after
        // our own successful save (the frame's only plausible delta is this
        // write).
        lastOwnSaveAtRef.current = Date.now();
        versionByPath.current.set(path, res.version);
        savedTextRef.current.set(path, buffer);
        const stillCurrent = buffersRef.current.get(path) === buffer;
        updateFileState(path, {
          saving: false,
          baseVersion: res.version,
          dirty: !stillCurrent,
          externalChange: null,
          notFound: false,
          autosaveSuspended: false,
          saveError: null,
        });
        return true;
      } catch (e) {
        if (e instanceof FileWriteConflictError) {
          // CAS rejected. Suspend autosave for this tab until the user
          // resolves; the banner/card renders from this.
          updateFileState(path, {
            saving: false,
            autosaveSuspended: true,
            externalChange: { currentVersion: e.currentVersion, currentContent: e.currentContent },
          });
        } else {
          console.error(`save(${path}) failed:`, e);
          updateFileState(path, {
            saving: false,
            autosaveSuspended: true,
            saveError: e instanceof Error ? e.message : String(e),
          });
        }
        return false;
      }
    },
    [writeFile, updateFileState]
  );

  const saveFile = useCallback(
    (path: string, kind: SaveKind): Promise<boolean> => {
      if (isHeld(path)) {
        const deferred = deferredSavesRef.current.get(path);
        if (deferred === undefined || deferred === 'autosave') deferredSavesRef.current.set(path, kind);
        return Promise.resolve(false);
      }
      const existing = savingPromisesRef.current.get(path);
      if (existing) return existing; // join the in-flight save (see savingPromisesRef doc)
      const run = doSaveFile(path, kind).finally(() => {
        savingPromisesRef.current.delete(path);
      });
      savingPromisesRef.current.set(path, run);
      return run;
    },
    [doSaveFile, isHeld]
  );

  const handleEdit = useCallback(
    (rawPath: string) => {
      const path = resolvePath(rawPath);
      const meta = fileStatesRef.current[path];
      if (meta && !meta.dirty) updateFileState(path, { dirty: true });
    },
    [updateFileState, resolvePath]
  );

  // Sync boundary (the pane's 1500 ms idle debounce, Cmd/Ctrl+S flush, tab
  // switch, close). This is ALSO the autosave trigger: the debounce IS
  // the idle detector, so autosave fires here unless the tab is suspended,
  // conflicted, or held (app-shell ADR-530ad — deferred until release).
  const handleBufferSync = useCallback(
    (rawPath: string, text: string) => {
      const path = resolvePath(rawPath);
      const meta = fileStatesRef.current[path];
      if (!meta) return;
      buffersRef.current.set(path, text);
      const clean = savedTextRef.current.get(path) === text;
      if (meta.dirty === clean) updateFileState(path, { dirty: !clean });
      if (
        !clean &&
        !meta.autosaveSuspended &&
        meta.externalChange === null &&
        !meta.notFound &&
        !savingPromisesRef.current.has(path)
      ) {
        void saveFile(path, 'autosave');
      }
    },
    [updateFileState, saveFile, resolvePath]
  );

  const handleManualSave = useCallback(
    (rawPath: string) => {
      void saveFile(resolvePath(rawPath), 'manual');
    },
    [saveFile, resolvePath]
  );

  const resolveEditing = useCallback(
    (rawPath: string, action: FileEditingResolveAction, mergedText?: string) => {
      const path = resolvePath(rawPath);
      const meta = fileStatesRef.current[path];
      if (!meta) return;
      switch (action) {
        case 'merged': {
          // Manual merge in the merge view: the merged text
          // becomes the DIRTY buffer; the merge was performed against theirs
          // at `currentVersion`, so that version becomes the CAS anchor — the
          // next save (manual or autosave, which this resolution re-enables)
          // targets the live version instead of re-conflicting on the stale
          // one.
          const ec = meta.externalChange;
          if (!ec || ec.currentVersion === null || ec.currentContent === undefined) {
            throw new Error(
              `merged(${path}): no incoming content/version available — this action should not have been offered`
            );
          }
          if (mergedText === undefined) {
            throw new Error(`merged(${path}): resolution requires the merged text`);
          }
          buffersRef.current.set(path, mergedText);
          savedTextRef.current.set(path, ec.currentContent);
          versionByPath.current.set(path, ec.currentVersion);
          updateFileState(path, {
            baseVersion: ec.currentVersion,
            dirty: mergedText !== ec.currentContent,
            externalChange: null,
            notFound: false,
            autosaveSuspended: false,
            saveError: null,
            epoch: meta.epoch + 1,
          });
          return;
        }
        case 'reload': {
          const ec = meta.externalChange;
          if (!ec || ec.currentVersion === null || ec.currentContent === undefined) {
            throw new Error(
              `reload(${path}): no incoming content available — this action should not have been offered`
            );
          }
          buffersRef.current.set(path, ec.currentContent);
          savedTextRef.current.set(path, ec.currentContent);
          versionByPath.current.set(path, ec.currentVersion);
          updateFileState(path, {
            baseVersion: ec.currentVersion,
            dirty: false,
            externalChange: null,
            notFound: false,
            autosaveSuspended: false,
            saveError: null,
            epoch: meta.epoch + 1,
          });
          return;
        }
        case 'keep-mine':
          void saveFile(path, 'keep-mine');
          return;
        case 'save-as-new':
          void saveFile(path, 'save-as-new');
          return;
        case 'retry-save':
          void saveFile(path, 'manual');
          return;
        case 'save-close':
          void saveFile(path, meta.notFound ? 'save-as-new' : 'manual').then((ok) => {
            if (ok) closeFile(path);
          });
          return;
        case 'discard-close':
          closeFile(path);
          return;
      }
    },
    [saveFile, closeFile, updateFileState, resolvePath]
  );

  // --- tree refresh controller ----------------------------------------------
  // The host renders <FileTree refreshRef={treeRefreshRef}>; refreshTree drives
  // it after re-listing the root itself.
  const treeRefreshRef = useRef<FileTreeRefreshHandle | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const openPathsRef = useRef<string[]>([]);
  openPathsRef.current = openPaths;
  const activePathRef = useRef<string | null>(null);
  activePathRef.current = activePath;

  const runRefresh = useCallback(
    async (manual: boolean): Promise<void> => {
      // Hibernation policy: AUTOMATIC triggers never wake a
      // hibernated cloud pod — skip; the existing notReady notice already
      // shows the staleness. A MANUAL refresh is an explicit user act and may
      // pay the resume cost.
      if (isLocal && state !== 'ready') return;
      if (!isLocal && state !== 'ready' && !manual) return;
      setRefreshing(true);
      setRefreshError(null);
      const epochAtStart = listingEpochRef.current;
      try {
        if (!isLocal && state !== 'ready') await resume();
        const entries = await listDir('');
        if (listingEpochRef.current !== epochAtStart) return;
        setRootEntries(entries);
        rememberSizes(entries);
        rememberVersions(entries);
        rememberListing('', entries);
        onEntriesDiscovered?.(entries);
        await treeRefreshRef.current?.refresh();
        // 3+4. Open-tab reconciliation:
        //    vanished paths flag `notFound` (never a silent close — closing
        //    would discard the buffer); version drift silently reloads CLEAN
        //    tabs and banners DIRTY ones (refresh never mutates a dirty
        //    buffer).
        // (app-shell ADR-530ad)
        if (listingEpochRef.current !== epochAtStart) return;
        for (const path of openPathsRef.current) {
          if (listingEpochRef.current !== epochAtStart) return;
          const meta = fileStatesRef.current[path];
          const idx = path.lastIndexOf('/');
          const parent = idx === -1 ? '' : path.slice(0, idx);
          const listing = listedChildrenByDir.current.get(parent);
          if (listing !== undefined && !listing.has(path)) {
            versionByPath.current.delete(path);
            if (meta && !meta.notFound) {
              // Delete-conflict treatment: the card offers
              // keep-buffer-and-save-as-new or close; autosave must not
              // silently recreate the file, so suspend it.
              updateFileState(path, { notFound: true, autosaveSuspended: true });
            }
            continue;
          }
          if (!meta) continue;
          if (meta.notFound && listing !== undefined) {
            updateFileState(path, {
              notFound: false,
              autosaveSuspended: meta.saveError !== null || meta.externalChange !== null,
            });
          }
          const latest = versionByPath.current.get(path);
          if (latest === undefined || meta.baseVersion === null || latest === meta.baseVersion)
            continue;
          if (meta.dirty) {
            if (meta.externalChange?.currentVersion !== latest) {
              updateFileState(path, { externalChange: { currentVersion: latest } });
            }
          } else {
            const r = await readFile(path);
            if (listingEpochRef.current !== epochAtStart) return;
            if (r.encoding === 'utf8') {
              buffersRef.current.set(path, r.content);
              savedTextRef.current.set(path, r.content);
              if (typeof r.version === 'string') versionByPath.current.set(path, r.version);
              updateFileState(path, {
                baseVersion: r.version ?? null,
                epoch: (fileStatesRef.current[path]?.epoch ?? 0) + 1,
              });
            }
          }
        }
      } catch (e) {
        setRefreshError(e instanceof Error ? e.message : String(e));
      } finally {
        setRefreshing(false);
      }
    },
    [
      isLocal,
      state,
      resume,
      listDir,
      readFile,
      rememberSizes,
      rememberVersions,
      rememberListing,
      onEntriesDiscovered,
      updateFileState,
    ]
  );
  const runRefreshRef = useRef(runRefresh);
  runRefreshRef.current = runRefresh;

  // Coalescing: one in-flight refresh shared by concurrent
  // callers, plus a single trailing-edge queued run — a burst (turn-end +
  // watcher push together) produces at most one in flight and one queued.
  // A queued manual request keeps its manual (resume-allowed) semantics.
  const inFlightRef = useRef<Promise<void> | null>(null);
  const queuedRef = useRef<{ promise: Promise<void>; manual: boolean } | null>(null);

  const refreshTree = useCallback((opts?: { manual?: boolean }): Promise<void> => {
    const manual = opts?.manual === true;
    const start = (runManual: boolean): Promise<void> => {
      const run = runRefreshRef.current(runManual).finally(() => {
        if (inFlightRef.current === run) inFlightRef.current = null;
      });
      inFlightRef.current = run;
      return run;
    };
    if (inFlightRef.current === null) return start(manual);
    const queued = queuedRef.current;
    if (queued !== null) {
      queued.manual = queued.manual || manual;
      return queued.promise;
    }
    const entry = { manual } as { promise: Promise<void>; manual: boolean };
    entry.promise = inFlightRef.current
      .catch(() => undefined) // the queued run proceeds regardless of the prior outcome
      .then(() => {
        queuedRef.current = null;
        return start(entry.manual);
      });
    queuedRef.current = entry;
    return entry.promise;
  }, []);

  const createFile = useCallback(
    async (path: string, initialContent = ''): Promise<void> => {
      const res = await createFileExclusive(path, initialContent);
      movedPathAliasRef.current.delete(path);
      lastOwnSaveAtRef.current = Date.now();
      versionByPath.current.set(path, res.version);
      buffersRef.current.set(path, initialContent);
      savedTextRef.current.set(path, initialContent);
      setOpenPaths((prev) => (prev.includes(path) ? prev : [...prev, path]));
      setFileStates((prev) => ({
        ...prev,
        [path]: { ...INITIAL_FILE_STATE, baseVersion: res.version },
      }));
      setActivePath(path);
      // Refresh the tree, listing the new file's parent FIRST: the refresh's
      // open-tab reconciliation flags a path `notFound` when its parent's
      // last-known listing lacks it — a stale parent listing would
      // immediately mis-flag the tab we just created.
      const idx = path.lastIndexOf('/');
      try {
        await loadChildren(idx === -1 ? '' : path.slice(0, idx));
      } finally {
        await refreshTree();
      }
    },
    [createFileExclusive, loadChildren, refreshTree]
  );

  const holdPaths = useCallback(async (prefix: string): Promise<void> => {
    heldPrefixesRef.current.add(prefix);
    const inFlight: Promise<boolean>[] = [];
    for (const [path, run] of savingPromisesRef.current) {
      if (path === prefix || path.startsWith(`${prefix}/`)) inFlight.push(run);
    }
    await Promise.all(inFlight);
  }, []);

  const releasePaths = useCallback(
    (prefix: string): void => {
      heldPrefixesRef.current.delete(prefix);
      for (const [rawPath, kind] of [...deferredSavesRef.current]) {
        const path = resolvePath(rawPath);
        if (isHeld(path)) continue;
        deferredSavesRef.current.delete(rawPath);
        const meta = fileStatesRef.current[path];
        if (!meta || meta.notFound) continue;
        if (kind === 'autosave' && (!meta.dirty || meta.autosaveSuspended || meta.externalChange !== null)) continue;
        void saveFile(path, kind);
      }
    },
    [resolvePath, isHeld, saveFile]
  );

  const markDeleted = useCallback(
    (prefix: string): void => {
      listingEpochRef.current += 1;
      for (const dir of [...listedChildrenByDir.current.keys()]) {
        if (dir === prefix || dir.startsWith(`${prefix}/`)) listedChildrenByDir.current.delete(dir);
      }
      const idx = prefix.lastIndexOf('/');
      listedChildrenByDir.current.get(idx === -1 ? '' : prefix.slice(0, idx))?.delete(prefix);
      for (const path of openPathsRef.current) {
        if (path !== prefix && !path.startsWith(`${prefix}/`)) continue;
        versionByPath.current.delete(path);
        deferredSavesRef.current.delete(path);
        updateFileState(path, { notFound: true, autosaveSuspended: true });
        const st = fileStatesRef.current[path];
        if (st) fileStatesRef.current = { ...fileStatesRef.current, [path]: { ...st, notFound: true, autosaveSuspended: true } };
      }
    },
    [updateFileState]
  );

  const followMovedPath = useCallback((from: string, to: string): void => {
    const remap = (path: string): string | null => {
      if (path === from) return to;
      if (path.startsWith(`${from}/`)) return `${to}${path.slice(from.length)}`;
      return null;
    };
    const moved = new Map<string, string>();
    for (const path of openPathsRef.current) {
      const next = remap(path);
      if (next !== null) moved.set(path, next);
    }
    listingEpochRef.current += 1;
    if (moved.size === 0) return;
    const rekey = <T,>(map: Map<string, T>) => {
      for (const [oldPath, newPath] of moved) {
        if (!map.has(oldPath)) continue;
        map.set(newPath, map.get(oldPath) as T);
        map.delete(oldPath);
      }
    };
    rekey(buffersRef.current);
    rekey(savedTextRef.current);
    rekey(versionByPath.current);
    rekey(sizeByPath.current);
    rekey(deferredSavesRef.current);
    for (const newPath of moved.values()) movedPathAliasRef.current.delete(newPath);
    for (const [oldPath, newPath] of moved) {
      movedPathAliasRef.current.set(oldPath, newPath);
      for (const [alias, target] of movedPathAliasRef.current) {
        if (target === oldPath) movedPathAliasRef.current.set(alias, newPath);
      }
    }
    const rekeyStates = (prev: Record<string, OpenFileState>) => {
      const next = { ...prev };
      for (const [oldPath, newPath] of moved) {
        const st = next[oldPath];
        if (st === undefined) continue;
        next[newPath] = st;
        delete next[oldPath];
      }
      return next;
    };
    fileStatesRef.current = rekeyStates(fileStatesRef.current);
    setFileStates(rekeyStates);
    openPathsRef.current = openPathsRef.current.map((p) => moved.get(p) ?? p);
    setOpenPaths((prev) => prev.map((p) => moved.get(p) ?? p));
    const active = activePathRef.current;
    if (active !== null && moved.has(active)) activePathRef.current = moved.get(active) as string;
    setActivePath((cur) => (cur !== null && moved.has(cur) ? (moved.get(cur) as string) : cur));
  }, []);

  // --- fast path: watcher-event active-tab re-check -------------------------
  // On a files-changed watcher event the host calls this IN ADDITION to
  // `refreshTree`: ONE bounded read of the ACTIVE tab detects a change the
  // listing diff can miss (the tab's parent dir may not be expanded) and acts
  // on it immediately — clean + version drift → silent reload from that same
  // read; dirty + drift → externalChange banner carrying the content the read
  // just returned (so reload/merge are offered without another round trip).
  // Non-active CLEAN tabs are only cache-busted (listed-version entry dropped
  // + epoch bump, no I/O) so the next activation re-reads. One read per event.
  const recheckActiveFile = useCallback(async (): Promise<void> => {
    if (isLocal && state !== 'ready') return;
    const active = activePathRef.current;
    for (const path of openPathsRef.current) {
      if (path === active) continue;
      const meta = fileStatesRef.current[path];
      if (!meta || meta.dirty) continue; // dirty buffers are never touched
      versionByPath.current.delete(path); // per-tab cache now misses on activation
      updateFileState(path, { epoch: meta.epoch + 1 });
    }
    if (!active) return;
    const meta = fileStatesRef.current[active];
    if (!meta || meta.baseVersion === null) return; // nothing loaded to compare against
    try {
      const epochAtStart = listingEpochRef.current;
      const r = await readFile(active);
      if (listingEpochRef.current !== epochAtStart) return;
      if (r.encoding !== 'utf8') return; // binary tabs carry no editable buffer
      const latest = r.version ?? null;
      if (latest === null || latest === meta.baseVersion) return; // unchanged
      versionByPath.current.set(active, latest);
      const cur = fileStatesRef.current[active];
      if (!cur) return; // tab closed while the read was in flight
      if (cur.dirty) {
        if (cur.externalChange?.currentVersion !== latest) {
          updateFileState(active, {
            externalChange: { currentVersion: latest, currentContent: r.content },
          });
        }
      } else {
        buffersRef.current.set(active, r.content);
        savedTextRef.current.set(active, r.content);
        updateFileState(active, {
          baseVersion: latest,
          notFound: false,
          epoch: cur.epoch + 1,
        });
      }
    } catch (e) {
      setRefreshError(e instanceof Error ? e.message : String(e));
    }
  }, [isLocal, state, readFile, updateFileState]);

  const editing: FileTabsEditing = useMemo(
    () => ({
      states: fileStates,
      // Editing works for BOTH modes (daemon CAS tool).
      readOnly: false,
      onEdit: handleEdit,
      onBufferSync: handleBufferSync,
      onSave: handleManualSave,
      onResolve: resolveEditing,
    }),
    [fileStates, handleEdit, handleBufferSync, handleManualSave, resolveEditing]
  );

  // Any dirty tab arms the exit guards (web beforeunload + the desktop
  // will-prevent-unload relay).
  const hasDirty = useMemo(() => Object.values(fileStates).some((s) => s.dirty), [fileStates]);

  const workstationHref = workspaceWorkstationHref(workspaceId);
  const notReady: FileBrowserNotice | null = useMemo(() => {
    if (state === 'no-pod')
      return {
        tone: 'muted',
        message:
          'This workspace has no running cloud pod to browse. Start or provision the workspace to browse its files.',
        canRetry: false,
      };
    if (state === 'other-computer')
      return {
        tone: 'warning',
        message: 'This workspace is set up on another computer that isn’t connected.',
        canRetry: false,
        action: { label: 'Move it to this computer', href: workstationHref },
      };
    if (state === 'this-computer-offline')
      return {
        tone: 'warning',
        message:
          'This computer isn’t connected to ShogunChop right now. Files will appear when it reconnects.',
        canRetry: false,
        action: { label: 'Workspace settings', href: workstationHref },
      };
    if (state === 'error')
      return {
        tone: 'error',
        message: `File tree unavailable: ${error ?? 'unknown error'}`,
        canRetry: false,
      };
    if (rootError)
      return {
        tone: 'error',
        message: `Could not list the workspace root: ${rootError}`,
        canRetry: false,
      };
    if (state === 'sleeping')
      return { tone: 'muted', message: 'The workspace is sleeping.', canRetry: true };
    if (state === 'waking')
      return { tone: 'muted', message: 'Waking the workspace…', canRetry: true };
    if (state === 'loading' || rootEntries === null) {
      return { tone: 'muted', message: 'Loading files…', canRetry: false };
    }
    return null;
  }, [state, error, rootError, rootEntries, workstationHref]);

  return {
    rootEntries,
    loadChildren,
    notReady,
    resume,
    // refresh controller
    refreshTree,
    refreshing,
    refreshError,
    treeRefreshRef,
    // watcher fast path + cloud-push self-echo currency
    recheckActiveFile,
    lastOwnSaveAtRef,
    read,
    openExternal,
    openLabel: platformType === 'desktop' ? 'Open' : 'Download',
    openPaths,
    activePath,
    activeSize: activePath ? sizeByPath.current.get(activePath) : undefined,
    openFile,
    closeFile,
    setActive,
    // new-file seam
    createFile,
    // context-menu file ops (fail-visible passthroughs to the SDK)
    deleteFile,
    moveFile,
    createFolder,
    holdPaths,
    releasePaths,
    followMovedPath,
    markDeleted,
    // editing surface
    editing,
    hasDirty,
    fileStates,
  };
}
